import { fromJS, Map, Record } from 'immutable';
import { cloneDeep, extend, flatten } from 'lodash';
import { v4 } from 'uuid';
import {
  decorateWithKeys,
  generateKeys,
  generateSchema,
  IChangeField,
  merger,
  setInModel,
} from '../../utils/helpers/model.helper';
import { IEntity } from './entity-data.data';
import { contentFactory } from './sub-models/content.model';
import { Header } from './sub-models/header.model';

export const CONTENT_KEY = 'content';
export const IS_DIRTY_KEY = 'is_dirty';
export const IS_NEW_KEY = 'is_new';
export const COMMON_FIELDS: any = {
  LOCAL_ID: '',
  ID: '',
  REVISION_ID: '',
  HEADER: Header,
};
export const COMMON_FIELDS_KEYS: any = generateKeys(COMMON_FIELDS);

export function generateContent(
  schema: any = {},
  schemaChanges: (props: Map<String, any>) => IChangeField[] = () => [],
  data: any = {}
): any {
  return new (contentFactory(schema, schemaChanges))(data);
}

export function generateSkeleton(parentSchema = {}, contentSchema = {}): any {
  let skeleton = extend(cloneDeep(COMMON_FIELDS), parentSchema);

  skeleton[CONTENT_KEY.toUpperCase()] = generateContent(contentSchema);

  return skeleton;
}

export function entityRecord(parentSchema = {}, contentSchema = {}) {
  let skeleton = generateSkeleton(parentSchema, contentSchema);

  return Record(generateSchema(skeleton));
}

export function entityKeys(parentSchema = {}, contentSchema = {}): any {
  let skeleton = generateSkeleton(parentSchema, contentSchema);

  return generateKeys(skeleton);
}

export function setInContent(
  map: Map<string, any>,
  key: string | string[],
  value: any
): Map<string, any> {
  return map.setIn(flatten([CONTENT_KEY, key]), value);
}

export function getInContent(map: Map<string, any>, key: string): any {
  return map.getIn([CONTENT_KEY, key]);
}

export function setInEntity(
  entity: Map<string, any>,
  parentChanges: IChangeField[] = [],
  entityKeys: any,
  currentContent: Map<string, any>,
  contentChanges: IChangeField[] = [],
  contentKeys: any
): Map<string, any> {
  const newContent = setInModel(currentContent, contentChanges);
  const newEntity = setInModel(
    entity.set(CONTENT_KEY, newContent),
    parentChanges
  );

  return decorateWithKeys(newEntity, {
    KEYS: entityKeys,
    CONTENT_KEYS: contentKeys,
  });
}

export function entityModelFactory(
  parentSchema = {},
  parentChanges: (props: Map<String, any>) => IChangeField[] = () => [],
  contentSchema = {},
  contentChanges: (contentProps: Map<String, any>) => IChangeField[] = () => []
) {
  const RECORD = entityRecord({
    ...parentSchema,
    [IS_NEW_KEY]: false,
    [IS_DIRTY_KEY]: false,
    [CONTENT_KEY]: contentSchema,
  });
  const KEYS = entityKeys(parentSchema, contentSchema);
  const CONTENT_KEYS = generateContent(contentSchema).KEYS;

  class Entity extends RECORD implements IEntity {
    KEYS: any;
    CONTENT_KEYS: any;

    constructor(props?: Map<string, any>) {
      props = props && props.toMap ? props.toMap() : fromJS(props || {});

      if (!props.get(KEYS.LOCAL_ID)) {
        props = props.set(KEYS.LOCAL_ID, v4());
      }

      props = props.set(
        KEYS.CONTENT,
        generateContent(
          contentSchema,
          contentChanges,
          props.get(KEYS.CONTENT, {})
        )
      );

      super(props);

      this.KEYS = KEYS;
      this.CONTENT_KEYS = CONTENT_KEYS;

      return this.setInEntity(
        parentChanges ? parentChanges(this) : [],
        contentChanges ? contentChanges(this.get(KEYS.CONTENT)) : []
      ) as Entity;
    }

    isDirty(): boolean {
      return this.get(IS_DIRTY_KEY, false);
    }

    isNew(): boolean {
      return this.get(IS_NEW_KEY, false);
    }

    get id(): string {
      return this.get(KEYS.ID);
    }

    get localId(): string {
      return this.get(KEYS.LOCAL_ID);
    }

    get revisionId(): string {
      return this.get(KEYS.REVISION_ID);
    }

    get header(): Header {
      const header = this.get(KEYS.HEADER);

      return header instanceof Header ? header : new Header(header);
    }

    get content(): any {
      return this.get(CONTENT_KEY);
    }

    setField(keys: string | string[], value: any) {
      return this.decorateWithKeys(this.setIn(flatten([keys]), value));
    }

    mergeField(keys: string | string[], value: Map<string, any>) {
      return this.decorateWithKeys(
        merger(this, this.setIn(flatten([keys]), value))
      );
    }

    getFieldInContent(keys: string | string[], defaultValue?: any) {
      return this.getIn(flatten([CONTENT_KEY, keys]), defaultValue || null);
    }

    setFieldInContent(keys: string | string[], value: any) {
      return this.setField(flatten([CONTENT_KEY, keys]), value);
    }

    mergeFieldInContent(keys: string | string[], value: Map<string, any>) {
      return this.mergeField(flatten([CONTENT_KEY, keys]), value);
    }

    mergeContent(value: Map<string, any>) {
      return this.mergeField(CONTENT_KEY, value);
    }

    setContent(contentData: Map<string, any>) {
      return this.setField(CONTENT_KEY, contentData);
    }

    setInEntity(
      parentChanges: IChangeField[] = [],
      contentChanges: IChangeField[] = []
    ): Map<string, any> {
      const defaultParentChanges = [
        {
          key: KEYS.HEADER,
          value: this.header,
        },
      ];

      return setInEntity(
        this,
        [...defaultParentChanges, ...parentChanges],
        KEYS,
        this.content,
        contentChanges,
        this.CONTENT_KEYS
      );
    }

    decorateWithKeys(entity: Map<string, any>) {
      return decorateWithKeys(entity, {
        KEYS: KEYS,
        CONTENT_KEYS: this.CONTENT_KEYS,
      });
    }

    serialize(): any {
      return this.toMap().withMutations((map) => {
        map.remove(IS_DIRTY_KEY).remove(IS_NEW_KEY);
      });
    }
  }

  return Entity;
}
